import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import chlorination from "../images/chlorination.jpeg";

const images = [
  {
    title: "Integrated Chlorination & Filtration Plant",
    desc: "Multi-stage chlorination and filtration unit designed for a residential society in Kolkata.",
    category: "Treatment Plants",
    src: chlorination,
    tall: true,
  },
  {
    title: "Industrial Water Treatment Unit",
    desc: "High-capacity treatment system handling daily water demand for a large apartment complex.",
    category: "Treatment Plants",
    src: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=900&q=80",
    tall: false,
  },
  {
    title: "Multi-Media Filter System",
    desc: "Pressure filtration vessels with layered sand, anthracite and gravel media for turbidity removal.",
    category: "Treatment Plants",
    src: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=900&q=80",
    tall: false,
  },
  {
    title: "Utility Water Distribution Network",
    desc: "Engineered piping layout integrating the treatment plant with overhead tanks and rising mains.",
    category: "Treatment Plants",
    src: "https://images.unsplash.com/photo-1584432810601-6c7f27d2362b?auto=format&fit=crop&w=900&q=80",
    tall: true,
  },
  {
    title: "Water Treatment Infrastructure",
    desc: "Full-scale water treatment infrastructure installed for a gated community in Newtown.",
    category: "Treatment Plants",
    src: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&w=900&q=80",
    tall: false,
  },
  {
    title: "Purified Drinking Water Quality",
    desc: "Output water quality after multi-stage RO and UV treatment — clear, safe and bacteria-free.",
    category: "Water Quality",
    src: "https://images.unsplash.com/photo-1559825481-12a05cc00344?auto=format&fit=crop&w=900&q=80",
    tall: false,
  },
  {
    title: "Natural Source Water Processing",
    desc: "Raw source water from borewell or natural supply before treatment — high iron and turbidity.",
    category: "Water Quality",
    src: "https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?auto=format&fit=crop&w=900&q=80",
    tall: true,
  },
  {
    title: "Clean Water for Every Resident",
    desc: "Consistent, safe water delivered to every flat and utility point across the residential building.",
    category: "Water Quality",
    src: "https://images.unsplash.com/photo-1511818966892-d7d671e672a2?auto=format&fit=crop&w=900&q=80",
    tall: false,
  },
  {
    title: "High-Rise Society Water Integration",
    desc: "Complete water treatment system integrated into the utility backbone of a high-rise building in Rajarhat.",
    category: "Residential Projects",
    src: "https://images.unsplash.com/photo-1513828583688-c52646db42da?auto=format&fit=crop&w=900&q=80",
    tall: false,
  },
  {
    title: "Premium Residential Development",
    desc: "Water treatment infrastructure commissioned for a luxury residential tower in Newtown Action Area.",
    category: "Residential Projects",
    src: "https://images.unsplash.com/photo-1460317442991-0ec209397118?auto=format&fit=crop&w=900&q=80",
    tall: true,
  },
  {
    title: "Township Water Infrastructure",
    desc: "Centralised water treatment plant serving a multi-building township with IoT-enabled monitoring.",
    category: "Residential Projects",
    src: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=900&q=80",
    tall: false,
  },
  {
    title: "Gated Community Water System",
    desc: "Custom-capacity softener and filtration system designed for a gated villa community near Kolkata.",
    category: "Residential Projects",
    src: "https://images.unsplash.com/photo-1519999482648-25049ddd37b1?auto=format&fit=crop&w=900&q=80",
    tall: false,
  },
];

const categories = ["All", "Treatment Plants", "Water Quality", "Residential Projects"];

export default function GalleryPage() {
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const filtered = active === "All" ? images : images.filter((img) => img.category === active);

  return (
    <div className="mx-auto max-w-7xl px-6 py-20">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="mx-auto max-w-4xl text-center"
      >
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-400">
          Gallery
        </p>
        <h1 className="mt-4 text-4xl font-bold md:text-6xl">
          Real Water Treatment Infrastructure
        </h1>
        <p className="mt-5 text-lg leading-8 text-slate-400">
          Filtration plants, RO systems, UV units and residential water
          infrastructure projects across Kolkata, Newtown and Rajarhat.
        </p>
      </motion.div>

      {/* Filter tabs */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="mt-10 flex flex-wrap justify-center gap-3"
      >
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActive(cat)}
            className={`rounded-full border px-5 py-2 text-sm font-semibold transition ${
              active === cat
                ? "border-cyan-400 bg-cyan-500/15 text-cyan-300"
                : "border-white/10 bg-white/5 text-slate-400 hover:border-white/20 hover:text-white"
            }`}
          >
            {cat}
          </button>
        ))}
      </motion.div>

      {/* Masonry grid */}
      <motion.div
        layout
        className="mt-12 columns-1 gap-6 sm:columns-2 xl:columns-3"
      >
        <AnimatePresence>
          {filtered.map((img, index) => (
            <motion.div
              key={img.title}
              layout
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="group mb-6 break-inside-avoid cursor-pointer overflow-hidden rounded-3xl border border-white/10 bg-white/5"
              onClick={() => setLightbox(img)}
            >
              <div className={`relative overflow-hidden ${img.tall ? "h-80" : "h-56"}`}>
                <img
                  src={img.src}
                  alt={img.title}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent opacity-0 transition duration-300 group-hover:opacity-100" />
                <div className="absolute bottom-0 left-0 right-0 translate-y-4 p-5 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                  <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400">
                    {img.category}
                  </p>
                  <p className="mt-1 text-sm leading-5 text-slate-300">{img.desc}</p>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-semibold text-white group-hover:text-cyan-300 transition">
                  {img.title}
                </h3>
                <p className="mt-1 text-xs font-medium text-slate-500">{img.category}</p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/95 px-4 backdrop-blur-xl"
            onClick={() => setLightbox(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="max-w-3xl w-full overflow-hidden rounded-[2rem] border border-white/15 bg-slate-900 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={lightbox.src}
                alt={lightbox.title}
                className="h-[55vh] w-full object-cover"
              />
              <div className="p-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400">
                  {lightbox.category}
                </p>
                <h2 className="mt-2 text-xl font-bold">{lightbox.title}</h2>
                <p className="mt-2 leading-6 text-slate-400">{lightbox.desc}</p>
                <button
                  onClick={() => setLightbox(null)}
                  className="mt-5 rounded-xl border border-white/10 bg-white/5 px-5 py-2.5 text-sm font-semibold transition hover:bg-white/10"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6 }}
        className="mt-20 rounded-[2rem] border border-white/10 bg-gradient-to-r from-cyan-500/10 via-slate-900/70 to-emerald-500/10 p-8 text-center md:p-12"
      >
        <h2 className="text-3xl font-bold md:text-4xl">
          Want a System Like This for Your Property?
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-300">
          We design and install water treatment plants, RO systems, UV units,
          iron removal and softener plants for residential projects across
          Kolkata, Newtown and Rajarhat.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/contact"
            className="rounded-2xl bg-cyan-500 px-7 py-3.5 font-semibold text-slate-950 transition hover:bg-cyan-400"
          >
            Get Free Proposal
          </Link>
          <a
            href="tel:+918697702088"
            className="rounded-2xl border border-white/15 bg-white/5 px-7 py-3.5 font-semibold transition hover:bg-white/10"
          >
            Call 8697702088
          </a>
        </div>
      </motion.div>
    </div>
  );
}
